import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { memoNamed } from '../utils/react/memo-named';
import {
  User,
  userInfoTagList,
  isUserInfo,
  Contact,
  contactWithDefault,
} from '../types/user';
import styled from 'styled-components';
import { NamesEditInfo } from './NamesEditInfo';
import { StringInput } from './common/input';

interface Props {
  docID: string;
  user: User;
  updateUserList: () => void;
}

const Root = styled.div`
  background-color: #d3cbc6;
  padding: 5px;
  border-radius: 5px;
`;

const Message = styled.div`
  color: red;
`;

const NamesEditHeader = styled.div`
  font-size: 11px;
`;

export const NamesEdit = memoNamed<Props>('NamesEdit', (props) => {
  const [userIsAdmin, setUserIsAdmin] = useState<boolean>(false);
  const [userIsAdminUpdating, setUserIsAdminUpdating] = useState<boolean>(
    false
  );
  const [userIsAdminMessage, setUserIsAdminMessage] = useState<string>('');
  const [userIsDead, setUserIsDead] = useState<boolean>(false);
  const [userIsDeadUpdating, setUserIsDeadUpdating] = useState<boolean>(false);
  const [userIsDeadMessage, setUserIsDeadMessage] = useState<string>('');

  const NamesEditInfoList = useMemo(
    () =>
      userInfoTagList.map((u) => {
        const userInfoOrOther = props.user[u.tagKey];
        if (isUserInfo(userInfoOrOther)) {
          return {
            tagKey: u.tagKey,
            tagName: u.tagName,
            privateIsDisabled: false,
            inputValue: userInfoOrOther.value,
            isPublic: userInfoOrOther.open,
            check: u.check,
          };
        } else {
          return {
            tagKey: u.tagKey,
            tagName: u.tagName,
            privateIsDisabled: true,
            inputValue: userInfoOrOther.toString(),
            isPublic: true,
            check: u.check,
          };
        }
      }),
    [props.user]
  );

  useEffect(() => {
    setUserIsAdmin(props.user.isAdmin);
    setUserIsDead(props.user.isDead);
    setContact(props.user.contact);
  }, [props.user.isAdmin, props.user.isDead, props.user.contact]);

  const updateUserIsAdmin = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const db = firebase.firestore();
      const isAdmin = event.target.value === 'isAdminTrue';
      setUserIsAdmin(isAdmin);
      setUserIsAdminUpdating(true);
      setUserIsAdminMessage('');
      db.collection('Users')
        .doc(props.docID)
        .update({
          isAdmin,
        })
        .then(() => {
          setUserIsAdminUpdating(false);
        })
        .catch((error) => {
          console.error(error);
          setUserIsAdminMessage('データの保存に失敗しました。');
          setUserIsAdminUpdating(false);
        });
    },
    [props.docID]
  );

  const updateUserIsDead = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const db = firebase.firestore();
      const isDead = event.target.value === 'true';
      setUserIsDead(isDead);
      setUserIsDeadUpdating(true);
      setUserIsDeadMessage('');
      db.collection('Users')
        .doc(props.docID)
        .update({
          isDead,
        })
        .then(() => {
          setUserIsDeadUpdating(false);
        })
        .catch((error) => {
          console.error(error);
          setUserIsDeadMessage('データの保存に失敗しました。');
          setUserIsDeadUpdating(false);
        });
    },
    [props.docID]
  );

  //Delete Account
  const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
  const [deleteMessage, setDeleteMessage] = useState<string>('');
  const [deleting, setDeleting] = useState<boolean>(false);

  const toggleConfirm = useCallback(() => {
    setDeleteConfirm((deleteConfirm) => !deleteConfirm);
  }, []);

  const deleteAccount = useCallback(() => {
    const db = firebase.firestore();
    setDeleting(true);
    db.collection('Users')
      .doc(props.docID)
      .delete()
      .then(() => {
        setDeleting(false);
        props.updateUserList();
      })
      .catch((error) => {
        console.error(error);
        setDeleting(false);
        setDeleteMessage('アカウントの削除に失敗しました。');
      });
  }, [props]);

  // Identification Update
  const [identification, setIdentification] = useState<string>(
    props.user.identification
  );
  const [identificationMessage, setIdentificationMessage] = useState<string>(
    ''
  );
  const [identificationUpdating, setIdentificationUpdating] = useState<boolean>(
    false
  );

  const updateIdentification = useCallback(() => {
    //TODO 6桁英小文字チェック
    //TODO 一致確認
    const db = firebase.firestore();
    setIdentificationUpdating(true);
    db.collection('Users')
      .where('identification', '==', identification)
      .get()
      .then((snapshot) => {
        if (snapshot.size === 0) {
          db.collection('Users')
            .doc(props.docID)
            .update({
              identification: identification,
            })
            .then(() => {
              setIdentificationUpdating(false);
              setIdentificationMessage('');
            })
            .catch((error) => {
              console.error(error);
              setIdentificationUpdating(false);
              setIdentificationMessage('データ更新時にエラーが発生しました。');
            });
        } else {
          setIdentificationUpdating(false);
          setIdentificationMessage(
            'その仮パスワードはすでに使用されています。'
          );
        }
      })
      .catch((error) => {
        console.error(error);
        setIdentificationUpdating(false);
        setIdentificationMessage(
          '仮パスワードの検査時にエラーが発生しました。'
        );
      });
    //TODO identification update
  }, [identification, props]);

  //contact
  const [contact, setContact] = useState<Contact>('none');
  const [contactUpdating, setContactUpdating] = useState<boolean>(false);
  const onContactChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const _contact = contactWithDefault(event.target.value);
      setContact(_contact);
      setContactUpdating(true);
      const db = firebase.firestore();
      db.collection('Users')
        .doc(props.docID)
        .update({
          contact: _contact,
        })
        .then(() => {
          setContactUpdating(false);
        })
        .catch((error) => {
          console.error(error);
          setContactUpdating(false);
        });
    },
    [props]
  );
  return (
    <div>
      <h3>名簿編集画面</h3>
      <Root>
        <button
          onClick={() => {
            props.updateUserList();
          }}
        >
          編集を終了する
        </button>
        <br />
        {NamesEditInfoList.map((u, i) => (
          <NamesEditInfo
            key={i}
            tagName={u.tagName}
            tagKey={u.tagKey}
            privateIsDisabled={u.privateIsDisabled}
            inputValue={u.inputValue}
            isPublic={u.isPublic}
            docID={props.docID}
            check={u.check}
          />
        ))}
        <NamesEditHeader>管理権限</NamesEditHeader>
        <select
          value={userIsAdmin ? 'isAdminTrue' : 'isAdminFalse'}
          onChange={updateUserIsAdmin}
        >
          <option value='isAdminTrue'>管理権限あり</option>
          <option value='isAdminFalse'>管理権限なし</option>
        </select>
        {userIsAdminUpdating ? ' ...通信中' : undefined}
        <Message>{userIsAdminMessage}</Message>
        <hr />
        <NamesEditHeader>生没</NamesEditHeader>
        <select
          value={userIsDead ? 'true' : 'false'}
          onChange={updateUserIsDead}
        >
          <option value='true'>没</option>
          <option value='false'>生</option>
        </select>
        {userIsDeadUpdating ? ' ...通信中' : undefined}
        <Message>{userIsDeadMessage}</Message>
        <hr />
        {props.user.userID === '' ? (
          <div>
            <NamesEditHeader>仮パスワード</NamesEditHeader>
            <StringInput
              value={identification}
              onValueChange={setIdentification}
              onBlur={updateIdentification}
            />
            {identificationUpdating ? ' 通信中...' : undefined}
            <Message>{identificationMessage}</Message>
            <hr />
          </div>
        ) : undefined}
        <NamesEditHeader>UserID</NamesEditHeader>
        <div>{props.user.userID === '' ? '無し' : props.user.userID}</div>
        <hr />
        <NamesEditHeader>docID</NamesEditHeader>
        <div>{props.docID}</div>
        <hr />
        <NamesEditHeader>桃源会からの連絡先</NamesEditHeader>
        <select value={contact} onChange={onContactChange}>
          <option value='email'>Email</option>
          <option value='current'>現住所</option>
          <option value='home'>帰省先</option>
          <option value='none'>送らない</option>
        </select>
        {contactUpdating ? ' 通信中...' : undefined}
        <hr />
        <NamesEditHeader>アカウントの削除</NamesEditHeader>
        {deleteConfirm ? (
          <div>
            本当に削除しますか？
            <button onClick={deleteAccount}>削除</button>
            {deleting ? (
              ' 通信中...'
            ) : (
              <button onClick={toggleConfirm}>いいえ</button>
            )}
            <Message>{deleteMessage}</Message>
          </div>
        ) : (
          <button onClick={toggleConfirm}>削除</button>
        )}
        <hr />
        <button
          onClick={() => {
            props.updateUserList();
          }}
        >
          編集を終了する
        </button>
      </Root>
    </div>
  );
});
